import axios from 'axios';
import firebase from 'firebase/app';
import 'firebase/auth';
import Vue from 'vue';
import router from "./../../router";

const TOKEN_KEY = 'pandavue-token';

const state = {
  token: localStorage.getItem(TOKEN_KEY) || '',

  user: undefined,
  firebaseUser: undefined,

  currentModule: undefined,
};

const getters = {
  token: (state) => state.token,
  isLoggedIn: (state) => state.token && state.user && state.firebaseUser,
  currentUser: (state) => state.user,
  currentModule: (state) => state.currentModule,
  isSuperAdmin: (state) => state.user && state.user.isSuperAdmin,
  hasAccess: (state) => (module, permissions, isSuperAdminRequired) => {
    if (!state.user) return false;

    // Super admin has access to everything
    if (state.user.isSuperAdmin) return true;

    // From here onwards, the user is not a super admin
    if (isSuperAdminRequired && !state.user.isSuperAdmin) return false;
    if (!state.user.permissions) return false;

    const perms = state.user.permissions[module];
    if (perms) {
      // If a permission is provided, check if we have the permission
      if (permissions) {
        // Make sure permission is array
        if (!Array.isArray(permissions)) {
          permissions = [permissions];
        }

        // The code 0 gives access to the whole module
        const hasAccess = perms.find(e => e === 0 || permissions.find(p => e === p));
        return hasAccess >= 0;

      } else {
        // Or check that we just have access to the module.
        return true;
      }

    } else {
      return false;
    }
  }
};

const mutations = {
  login(state, payload) {
    // Set the local state
    if (payload.token) Vue.set(state, 'token', payload.token);
    if (payload.user) Vue.set(state, 'user', payload.user);

    if (payload.firebaseUser) Vue.set(state, 'firebaseUser', payload.firebaseUser);

    // Set the local storage for the interceptor
    // and to allow the user to come back without login again
    if (payload.token) localStorage.setItem(TOKEN_KEY, payload.token);
  },
  logout(state) {
    // Clear local state
    state.token = '';
    state.firebaseUser = null;
    state.user = null;

    // Clear the local storage to avoid re-login on refresh
    localStorage.removeItem(TOKEN_KEY);

    firebase.auth().signOut().then();
  },
  updateCurrentModule(state, moduleCode) {
    Vue.set(state, 'currentModule', moduleCode);
  }
};

const actions = {
  login({ commit, dispatch }, user) {
    // Login with Firebase
    return dispatch('firebaseLogin', user).then(async ({ userCredential, firebaseToken }) => {

      // Login with WWF for the permission
      // The token return contains the Firebase Token and the WWF Token combined
      // The user returned is the WWF user containing the permissions and is super admin status
      const wwfLogin = await dispatch('wwfLogin', firebaseToken);

      // If the WWF Login is successful, we can authenticate the user and access the app
      if (wwfLogin.user) {
        commit('login', {
          token: firebaseToken,
          user: wwfLogin.user,
          firebaseUser: userCredential.user
        });

        return;

      } else {
        console.error('WWF LOGIN ERROR');

        // Make sure we clear the state
        commit('logout');
        throw { message: 'Error login.', code: 401 };
      }
    }).catch((err) => {
      console.error('FIREBASE LOGIN ERROR', err);

      // Make sure we clear the state
      commit('logout');
      const message = err.message ? err.message : 'Error login.';
      throw { message, code: 401 };
    });
  },
  firebaseLogin(state, user) {
    return new Promise((resolve, reject) => {
      firebase
        .auth()
        .signInWithEmailAndPassword(user.email, user.password)
        .then((userCredential) => {
          userCredential.user
            .getIdToken()
            .then((firebaseToken) => {
              resolve({ userCredential, firebaseToken })
            })
            .catch((err) => (reject(err)));
        })
        .catch((err) => (reject(err)));
    });
  },
  wwfLogin() {
    return axios.post(`/auth/login`)
      .then(({ data }) => (data))
      .catch(err => { throw err; });
  },
  logout({ commit }) {
    commit('logout');
    router.push('/login').catch(() => { });
  },
  refresh({ commit, dispatch, getters }, forceRefresh) {
    // This method will refresh the tokens and users based on the current logged in Firebase User
    // Usefull to refresh the permissions array for the WWF User
    return new Promise((resolve, reject) => {
      if (firebase.auth().currentUser) {

        // Reload the info about the user to make sure he's still enabled
        dispatch('getToken').then(async newFirebaseToken => {

          if (forceRefresh || !getters.isLoggedIn) {
            console.log('Refresh perms');

            const wwfLogin = await dispatch('wwfLogin');

            // If the WWF Login is successful, we can authenticate the user and access the app
            if (wwfLogin.user) {
              commit('login', {
                token: newFirebaseToken,
                user: wwfLogin.user,
                firebaseUser: firebase.auth().currentUser
              });

            } else {
              return reject({ message: 'Error WWF Login' });
            }
          }

          return resolve();
        })
          .catch((err) => {
            console.error('FIREBASE GET TOKEN ERROR - ', err);
            dispatch('logout');
            return reject({ message: 'No Firebase Token' });
          });
      } else {
        dispatch('logout');
        return reject({ message: 'No Firebase User' });
      }
    });
  },
  canActivate({ dispatch, getters, commit }, { module, isSuperAdmin }) {
    // If we change module, we force the refresh
    const currentModule = getters.currentModule;
    const forceRefresh = currentModule !== module;

    return dispatch('refresh', forceRefresh)
      .then(() => {
        commit('updateCurrentModule', module);
        return dispatch('validatePermissions', { module, isSuperAdmin })
      })
      .catch(err => { throw err });
  },
  validatePermissions({ dispatch, getters }, { module, isSuperAdmin }) {
    return new Promise((resolve, reject) => {
      try {
        if (!module && !isSuperAdmin) {
          // If nothing is precised, authorized
          return resolve();

        } else if ((module || isSuperAdmin) && getters.hasAccess(module, undefined, isSuperAdmin)) {
          // If the module or the super admin is defined, validate against the user
          return resolve();

        } else {

          // Lands here if the previous failed and therefore, the user don't have access to this module
          dispatch('notifications/error', {
            headline: 'No Access',
            details:
              "You don't have access to this module. Please try again later or contact an administrator.",
          }, { root: true }).then(() => {
            return reject({ message: 'No access', noAccess: true });
          });
        }

      } catch (err) {
        console.error('Validate Permissions ERROR', err);
        return reject({ message: 'No access', noAccess: true });
      }
    });
  },
  getToken() {
    return firebase.auth().currentUser.reload()
      .then(() => {
        return firebase.auth().currentUser.getIdToken()
      })

    // .catch((err) => {
    //   console.error('FIREBASE RELOAD USER ERROR - ', err);
    //   dispatch('logout');
    //   return reject({ message: 'Firebase Reload User' });
    // });
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
